export interface Membership {
  organization_id: string;
  organization_slug: string;
  organization_name: string;
  role: "owner" | "admin" | "member";
}

export interface Me {
  user: { id: string; display_name: string; avatar_url: string | null };
  memberships: readonly Membership[];
  selected_organization_id: string | null;
  csrf_token: string;
  billing_available: boolean;
  mcp_available?: boolean;
}

export interface Site {
  id: string;
  name: string;
  origin: string;
  primary_url: string;
  enabled: boolean;
  open_note_count: number;
  note_count: number;
  last_activity_at: string;
}

export interface Reply {
  id: string;
  body: string;
  author: string;
  author_name?: string;
  created_at: string;
}

export interface Note {
  id: string;
  site_id: string;
  site_name?: string;
  number: number;
  page_url: string;
  body: string;
  author: string;
  author_name?: string;
  state: "open" | "resolved";
  created_at: string;
  updated_at: string;
  replies: readonly Reply[];
}

export interface ApiKey {
  id: string;
  name: string;
  agent_name: string | null;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export interface GuestLink {
  id: string;
  site_id: string;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface TeamMember {
  user_id: string;
  display_name: string;
  avatar_url: string | null;
  role: "owner" | "admin" | "member";
}

export interface BillingSummary {
  offer_id: "appnotes_free_v1" | "appnotes_team_v1";
  billing_state: "free" | "active" | "grace" | "ended";
  member_cap: number | null;
  active_site_cap: number | null;
  active_sites_this_month: number;
  renews_at: string | null;
  grace_ends_at: string | null;
  cancel_at_period_end: boolean;
  can_upgrade: boolean;
  can_manage_billing: boolean;
  has_conflict: boolean;
  failed_events: readonly {
    id: string;
    kind: string;
    attempts: number;
    received_at: string;
    safe_error: string;
  }[];
}

export type MembershipManagement = "appnotes" | "identity_provider";

export class ApiError extends Error {
  public constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
  ) {
    super(message);
  }
}

/** Options for one same-origin console request. */
export interface ApiRequestOptions {
  readonly method?: "GET" | "POST" | "PATCH" | "DELETE";
  readonly body?: unknown;
  readonly csrf?: string;
  readonly idempotencyKey?: string;
  readonly timeoutMs?: number;
  readonly signal?: AbortSignal;
}

/** Console boundary: request failures retain the existing ApiError UI contract. */
export async function api<T>(
  path: string,
  input: ApiRequestOptions = {},
): Promise<T> {
  const headers = new Headers({ accept: "application/json" });
  if (input.body !== undefined) headers.set("content-type", "application/json");
  if (input.csrf !== undefined) headers.set("x-appnotes-csrf", input.csrf);
  if (input.idempotencyKey !== undefined) {
    headers.set("idempotency-key", input.idempotencyKey);
  }
  const controller = new AbortController();
  const cancel = () => controller.abort();
  input.signal?.addEventListener("abort", cancel, { once: true });
  if (input.signal?.aborted) cancel();
  let timedOut = false;
  const timeout = globalThis.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, input.timeoutMs ?? 20_000);
  const retryGuidance =
    input.idempotencyKey === undefined && input.method === "POST"
      ? "Check the latest state before trying again."
      : "Try again; completed actions will not be duplicated.";
  try {
    const response = await fetch(path, {
      method: input.method ?? "GET",
      credentials: "same-origin",
      headers,
      signal: controller.signal,
      ...(input.body === undefined ? {} : { body: JSON.stringify(input.body) }),
    });
    // Reading the body is part of the request deadline. Headers can arrive
    // successfully while the body stalls or the user leaves the page.
    if (!response.ok) {
      const payload: unknown = await response.json().catch(() => null);
      controller.signal.throwIfAborted();
      const detail =
        typeof payload === "object" && payload !== null && "error" in payload
          ? payload.error
          : null;
      throw new ApiError(
        typeof detail === "object" &&
          detail !== null &&
          "message" in detail &&
          typeof detail.message === "string"
          ? detail.message
          : "The request could not be completed.",
        response.status,
        typeof detail === "object" &&
          detail !== null &&
          "code" in detail &&
          typeof detail.code === "string"
          ? detail.code
          : "request_failed",
      );
    }
    const payload: unknown =
      response.status === 204 ? undefined : await response.json();
    controller.signal.throwIfAborted();
    // SAFETY: The existing console client assigns the endpoint's response type
    // at the call site. Runtime response schema parsing is outside this change.
    return payload as T;
  } catch (cause) {
    if (controller.signal.aborted) {
      throw new ApiError(
        timedOut
          ? `The request timed out. ${retryGuidance}`
          : "The request was canceled.",
        0,
        timedOut ? "request_timeout" : "request_cancelled",
      );
    }
    if (cause instanceof ApiError) throw cause;
    if (cause instanceof SyntaxError) {
      throw new ApiError(
        `The server returned an unreadable response. ${retryGuidance}`,
        0,
        "invalid_response",
      );
    }
    throw new ApiError(
      `The connection was interrupted. ${retryGuidance}`,
      0,
      cause instanceof TypeError ? "network_error" : "request_failed",
    );
  } finally {
    globalThis.clearTimeout(timeout);
    input.signal?.removeEventListener("abort", cancel);
  }
}
